import { buildHref } from "@softmax/web-addresses";
import { useTrackedSignOut } from "@softmax/web-features/telemetry";
import {
  analyticsEvents,
  analyticsSources,
} from "@softmax/web-features/telemetry/events";
import { accountSettingsHref } from "@softmax/web-features/site";
import type { PillMenuData } from "./use-pill-menu";
import { MenuItem } from "./menu/menu-item";
import { MenuRetry } from "./menu/menu-retry";
import { AccountCreditBalance } from "./menu/account-credit-balance";
import joinedStyles from "./account-pill.module.css";

export function SiteHeaderAccountMenu({
  data,
  member,
  onNavigate,
}: {
  readonly data: PillMenuData;
  readonly member: boolean;
  readonly onNavigate: (href: string) => void;
}) {
  const styles = joinedStyles;
  const signOut = useTrackedSignOut({
    event: analyticsEvents.siteNavAccountMenuSignOutClicked,
    properties: { source: analyticsSources.siteNavAccountMenu },
  });
  const links = member
    ? [
        { label: "Account settings", href: accountSettingsHref },
        { label: "Manage Coworlds", href: buildHref({ name: "coworlds" }) },
      ]
    : [
        {
          label: "Sign in",
          href: buildHref({
            name: "signIn",
            search: new URLSearchParams({ redirect_url: data.pathname }),
          }),
        },
      ];
  return (
    <ul className={joinedStyles.menu} aria-label="Account">
      {member && <AccountCreditBalance />}
      {links.map((link) => (
        <MenuItem
          key={link.label}
          link={link}
          styles={styles}
          onNavigate={onNavigate}
        />
      ))}
      {data.account.isError && (
        <MenuRetry
          message="Could not load account details."
          label="Retry account details"
          onRetry={() => void data.account.refetch()}
          styles={styles}
        />
      )}
      {data.playersError && (
        <MenuRetry
          message="Could not load your player."
          label="Retry player"
          onRetry={() => void data.refreshPlayers()}
          styles={styles}
        />
      )}
      {member && (
        <li>
          <button
            type="button"
            className={styles.item}
            disabled={signOut.isPending}
            onClick={() => signOut.mutate()}
          >
            {signOut.isPending ? "Signing out…" : "Sign out"}
          </button>
          {signOut.isError && (
            <output className={styles.heading}>
              Could not sign out. Please try again.
            </output>
          )}
        </li>
      )}
    </ul>
  );
}
